(ns practitest.frontend.common.ai-text-enhancement
  (:require [ajax.core :as ajax]
            [clojure.string :as str]
            [practitest.frontend.common.dom :as dom]
            [practitest.frontend.common.format-utils :as format-utils]
            [practitest.frontend.common.i18n :refer [get-translation]]
            [practitest.frontend.common.mixpanel :as mixpanel]
            [practitest.frontend.components.icons :as icons]
            [practitest.frontend.components.markdown3 :refer [markdown-preview]]
            [practitest.frontend.components.notifications :as notifications]
            [practitest.frontend.components.tooltip :as tooltip]
            [practitest.frontend.components2.buttons :as buttons]
            [practitest.frontend.components2.dropdown :as dropdown]
            [practitest.frontend.components2.spinners :as spinners]
            [practitest.frontend.routing.backend-routes :as broutes]
            [practitest.frontend.subs :as subs]
            [practitest.frontend.tests.test-form.common :refer [next-item
                                                                prev-item
                                                                new-state-id]]
            [re-frame.core :as re-frame]))

(defn- get-ai-enhancement-base-path
  "Get the base path for AI enhancement state"
  [form-id context-id field-id]
  [:form-states2 form-id :ai-enhancement context-id field-id])


(defn- get-current-text
  "Get current text from the appropriate location based on context"
  [db form-id context-id field-id]
  (cond
    (= context-id :preconditions)
    (get-in db [:form-states2 form-id :fields :preconditions :value])


    ;; Regular entity description field
    (= context-id :main)
    (get-in db [:form-states2 form-id :fields field-id :value])


    ;; Step field
    :else
    (get-in db [:form-states2 form-id :steps context-id :fields field-id :value])))


(defn- set-field-value
  "Set field value and dirty flag at the appropriate location"
  [db form-id context-id field-id value]
  (let [value-path (cond
                     (= context-id :preconditions)
                     [:form-states2 form-id :fields :preconditions :value]


                     (= context-id :main)
                     [:form-states2 form-id :fields field-id :value]


                     :else
                     [:form-states2 form-id :steps context-id :fields field-id :value])
        dirty-path (conj (vec (butlast value-path)) :dirty?)]
    (-> db
        (assoc-in value-path value)
        (assoc-in dirty-path true))))


(defn- get-current-ai-state
  [db form-id context-id field-id]
  (let [base-path (get-ai-enhancement-base-path form-id context-id field-id)
        enhancement-state (get-in db base-path)
        current-state-id (:current-state-id enhancement-state)]
    (get-in enhancement-state [:states current-state-id])))

;;; Subscriptions

(re-frame/reg-sub
  ::ai-enhancement-state
  (fn [db [_ form-id context-id field-id]]
    (get-current-ai-state db form-id context-id field-id)))

(re-frame/reg-sub
  ::ai-enhancement-prev-state-id
  (fn [db [_ form-id context-id field-id]]
    (let [base-path (get-ai-enhancement-base-path form-id context-id field-id)
          {:keys [states-list current-state-id]} (get-in db base-path)]
      (prev-item states-list current-state-id))))

(re-frame/reg-sub
  ::ai-enhancement-next-state-id
  (fn [db [_ form-id context-id field-id]]
    (let [base-path (get-ai-enhancement-base-path form-id context-id field-id)
          {:keys [states-list current-state-id]} (get-in db base-path)]
      (next-item states-list current-state-id))))

(re-frame/reg-sub
  ::ai-enhancement-current-user-prompt
  (fn [db [_ form-id context-id field-id]]
    (get-in db (conj (get-ai-enhancement-base-path form-id context-id field-id) :user-prompt))))

;;; Events

(re-frame/reg-event-db
  ::update-user-prompt
  (fn [db [_ form-id context-id field-id value]]
    (assoc-in db (conj (get-ai-enhancement-base-path form-id context-id field-id) :user-prompt) value)))

(re-frame/reg-event-db
  ::track-enhancement-request
  (fn [db [_ form-id context-id field-id xhrio]]
    (assoc-in db (conj (get-ai-enhancement-base-path form-id context-id field-id) :pending-request) xhrio)))

(re-frame/reg-event-db
  ::set-state-id
  (fn [db [_ form-id context-id field-id state-id]]
    (assoc-in db (conj (get-ai-enhancement-base-path form-id context-id field-id) :current-state-id) state-id)))

(re-frame/reg-event-fx
  ::abort-enhancement-request
  (fn [{db :db} [_ form-id context-id field-id]]
    (when-let [xhrio (get-in db (conj (get-ai-enhancement-base-path form-id context-id field-id) :pending-request))]
      (ajax/abort xhrio))
    {}))

(re-frame/reg-event-fx
  ::enhance-text
  (fn [{db :db} [_ form-id context-id field-id action-type]]
    (let [current-text (get-current-text db form-id context-id field-id)
          project-id (subs/get-current-project-id db)
          state-id (new-state-id)
          base-path (get-ai-enhancement-base-path form-id context-id field-id)]
      {:db (-> db
               (assoc-in base-path
                         {:current-state-id state-id
                          :states-list [state-id]
                          :states {state-id {:loading? true
                                             :action-type action-type
                                             :original-text current-text}}
                          :user-prompt ""})
               (update :progress conj ::enhance-text))
       :http-xhrio {:method          :post
                    :uri             (broutes/get-url "text_enhancements#enhance_text"
                                                      {:project_id project-id})
                    :params          {:text current-text
                                      :action_type (name action-type)}
                    :format          (ajax/json-request-format)
                    :response-format (ajax/json-response-format {:keywords? true})
                    :on-success      [::enhance-text-success form-id context-id field-id]
                    :on-failure      [::enhance-text-failed form-id context-id field-id]}})))

(re-frame/reg-event-fx
  ::enhance-text-success
  (fn [{db :db} [_ form-id context-id field-id response]]
    (let [base-path (get-ai-enhancement-base-path form-id context-id field-id)
          state-exists? (get-in db base-path)
          current-state-id (get-in db (conj base-path :current-state-id))
          current-state-path (conj base-path :states current-state-id)
          db (-> db
                 (update :progress disj ::enhance-text))
          {:keys [enhanced_text error warning]} response]
      (cond
        ;; If user dismissed the enhancement (clicked Collapse), don't show result
        (not state-exists?)
        {:db db}

        (or error warning)
        {:db (update-in db [:form-states2 form-id :ai-enhancement context-id] dissoc field-id)
         :dispatch [(if error ::notifications/error2 ::notifications/warning2)
                    (or error warning)
                    {:auto-close 5000}]}

        :else
        {:db (-> db
                 (assoc-in (conj current-state-path :loading?) false)
                 (assoc-in (conj current-state-path :enhanced-text) enhanced_text))}))))

(re-frame/reg-event-fx
  ::enhance-text-failed
  (fn [{db :db} [_ form-id context-id field-id response]]
    {:db (-> db
             (update :progress disj ::enhance-text)
             (update-in [:form-states2 form-id :ai-enhancement context-id] dissoc field-id))
     :dispatch [::notifications/error2
                (get-translation "generic.error.network-error" "Error fetching data from server")
                {:auto-close 5000}]}))

(re-frame/reg-event-fx
  ::apply-custom-prompt
  (fn [{db :db} [_ form-id context-id field-id]]
    (let [base-path (get-ai-enhancement-base-path form-id context-id field-id)
          enhancement-state (get-in db base-path)
          current-state (get-current-ai-state db form-id context-id field-id)
          current-text (:enhanced-text current-state)
          user-prompt (:user-prompt enhancement-state)
          project-id (subs/get-current-project-id db)
          state-id (new-state-id)]
      {:db (-> db
               (update-in (conj base-path :states-list) conj state-id)
               (assoc-in (conj base-path :current-state-id) state-id)
               (assoc-in (conj base-path :states state-id) {:loading? true})
               (update :progress conj ::apply-custom-prompt))
       :http-xhrio {:method          :post
                    :uri             (broutes/get-url "text_enhancements#custom_enhance_text"
                                                      {:project_id project-id})
                    :params          {:text current-text
                                      :custom_prompt user-prompt}
                    :format          (ajax/json-request-format)
                    :response-format (ajax/json-response-format {:keywords? true})
                    :on-success      [::custom-enhance-completed form-id context-id field-id]
                    :on-request      [::track-enhancement-request form-id context-id field-id]
                    :on-failure      [::custom-enhance-failed form-id context-id field-id]}})))

(re-frame/reg-event-fx
  ::custom-enhance-completed
  (fn [{db :db} [_ form-id context-id field-id response]]
    (let [base-path (get-ai-enhancement-base-path form-id context-id field-id)
          state-exists? (get-in db base-path)
          current-state-id (get-in db (conj base-path :current-state-id))
          current-state-path (conj base-path :states current-state-id)
          db (-> db
                 (update :progress disj ::apply-custom-prompt)
                 (update-in base-path dissoc :pending-request))
          {:keys [enhanced_text error warning]} response]

      (cond
        ;; If user dismissed the enhancement, don't show result
        (not state-exists?)
        {:db db}

        (or error warning)
        (let [current-states-list (get-in db (conj base-path :states-list))
              new-states-list (vec (butlast current-states-list))]
          {:db (-> db
                   (assoc-in (conj base-path :states-list) new-states-list)
                   (assoc-in (conj base-path :current-state-id) (last new-states-list)))
           :dispatch [(if error ::notifications/error2 ::notifications/warning2)
                      (or error warning)
                      {:auto-close 15000}]})

        :else
        {:db (-> db
                 (assoc-in (conj current-state-path :loading?) false)
                 (assoc-in (conj current-state-path :enhanced-text) enhanced_text)
                 (assoc-in (conj current-state-path :action-type) :custom)
                 (assoc-in (conj base-path :user-prompt) ""))}))))

(re-frame/reg-event-fx
  ::custom-enhance-failed
  (fn [{db :db} [_ form-id context-id field-id response]]
    (let [base-path (get-ai-enhancement-base-path form-id context-id field-id)
          current-states-list (get-in db (conj base-path :states-list))
          new-states-list (vec (butlast current-states-list))
          db (-> db
                 (update :progress disj ::apply-custom-prompt)
                 (update-in base-path dissoc :pending-request)
                 (assoc-in (conj base-path :states-list) new-states-list)
                 (assoc-in (conj base-path :current-state-id) (last new-states-list)))]
      (if (= :aborted (:failure response))
        {:db db}
        {:db db
         :dispatch [::notifications/error2
                    (get-translation "generic.error.network-error" "Error fetching data from server")
                    {:auto-close 5000}]}))))

(re-frame/reg-event-db
  ::apply-enhanced-text
  (fn [db [_ form-id context-id field-id action-type]]
    (let [current-state (get-current-ai-state db form-id context-id field-id)
          enhanced-text (:enhanced-text current-state)
          current-text (get-current-text db form-id context-id field-id)
          new-value (case action-type
                      "replace" enhanced-text
                      "insert" (str current-text "\n\n" enhanced-text)
                      enhanced-text)]
      (-> db
          (set-field-value form-id context-id field-id new-value)
          (assoc-in [:form-states2 form-id :ai-text-enhancement-used?] true)
          ;; Exit fullscreen by clearing state
          (update-in [:form-states2 form-id :ai-enhancement context-id] dissoc field-id)))))

(re-frame/reg-event-db
  ::discard-enhanced-text
  (fn [db [_ form-id context-id field-id]]
    ;; Exit fullscreen by clearing state
    (update-in db [:form-states2 form-id :ai-enhancement context-id] dissoc field-id)))

(re-frame/reg-event-db
  ::exit-enhancement-fullscreen
  (fn [db [_ form-id context-id field-id]]
    (update-in db [:form-states2 form-id :ai-enhancement context-id] dissoc field-id)))

;;; UI Components

(defn- ai-enhancement-footer
  "Footer component showing enhanced text with Apply/Discard actions, undo/redo navigation, and custom prompt input."
  [{:keys [form-id context-id field-id text loading? user-prompt
           prev-state-id next-state-id loading-ref]}]
  [:div.ai-enhancement-footer-container
   [:div.ai-enhancement-footer-frame
    [:div.ai-enhancement-footer-icon
     [icons/mux-smart-fox2]]
    [:div.ai-enhancement-footer-text
     (if loading?
       [:div.smartfox-loading-container
        {:ref loading-ref}
        [spinners/mux-spinner
         {:size :medium}]
        [:div
         (get-translation "text.enhance-with-ai.loading" "Please Wait .... Generating Text")]]
       [markdown-preview text])]
    [:div.ai-enhancement-footer-buttons
     [:div.ai-enhancement-navigation-buttons
      [buttons/icon-button
       {:icon [icons/mux-undo]
        :disabled? (or loading? (nil? prev-state-id))
        :on-click (fn [e]
                    (re-frame/dispatch [::set-state-id form-id context-id field-id prev-state-id]))}]
      [buttons/icon-button
       {:icon [icons/mux-redo]
        :disabled? (or loading? (nil? next-state-id))
        :on-click (fn [e]
                    (re-frame/dispatch [::set-state-id form-id context-id field-id next-state-id]))}]]
     [:div.ai-enhancement-action-buttons
      [buttons/button
       {:text (get-translation "generic.button.discard" "Discard")
        :icon [icons/x-circle]
        :tertiary? true
        :disabled? loading?
        :on-click #(re-frame/dispatch [::discard-enhanced-text form-id context-id field-id])}]


      [:div.ai-enhancement-split-button
       [buttons/button
        {:text (get-translation "text.enhance-with-ai.replace-text" "Replace Text")
         :icon [icons/mux-replace]
         :primary? true
         :disabled? loading?
         :class "split-button-main"
         :on-click #(re-frame/dispatch [::apply-enhanced-text form-id context-id field-id "replace"])}]


       [dropdown/dropdown-button
        {:icon-only? true
         :primary? true
         :disabled? loading?
         :class "split-button-dropdown"}
        {:placement "bottom-end"}


        [dropdown/dropdown-menu-item
         {:text (get-translation "text.enhance-with-ai.insert-text-below" "Insert text below")
          :icon [icons/mux-reg-plus]
          :on-click #(re-frame/dispatch [::apply-enhanced-text form-id context-id field-id "insert"])}]]]]]
    [:div.smartfox-modal-footer
     [:div.smartfox-input-prompt-container
      [:input.m-input
       {:value (or user-prompt "")
        :placeholder (get-translation "text.enhance-with-ai.user-prompt-placeholder"
                                      "Please tell Smart Fox what do you want to write next")
        :disabled loading?
        :on-mouse-down (fn [e]
                         (dom/stop-propagation e))
        :on-change (fn [e]
                     (re-frame/dispatch [::update-user-prompt form-id context-id field-id (dom/event-value e)]))}]
      [buttons/button
       {:primary? true
        :disabled? (str/blank? (or user-prompt ""))
        :on-click (fn [e]
                    (if loading?
                      (re-frame/dispatch [::abort-enhancement-request form-id context-id field-id])
                      (re-frame/dispatch [::apply-custom-prompt form-id context-id field-id])))
        :icon (if loading?
                [icons/mux-stop-icon]
                [icons/mux-arrow-right])}]]]]])

(defn- enhance-with-ai-dropdown
  "Dropdown button with AI enhancement options (Refine/Shorten/Elaborate)."
  [{:keys [form-id context-id field-id text has-permissions?]}]
  (let [text-too-short? (not (format-utils/has-enough-text? text))
        disabled? (or (not has-permissions?) text-too-short?)
        tooltip-text (cond
                       (not has-permissions?)
                       (get-translation "text.enhance-with-ai.no-permission"
                                        "You do not have the permission to edit this field")


                       text-too-short?
                       (get-translation "text.enhance-with-ai.text-too-short"
                                        "Please add more text to use AI enhancement")


                       :else
                       nil)
        button [dropdown/dropdown-button
                {:text (get-translation "text.enhance-with-ai" "Enhance with AI")
                 :tertiary? true
                 :icon [icons/mux-smart-fox2]
                 :disabled? disabled?}
                {:placement "top-start"
                 :flip true}
                [dropdown/dropdown-menu-item
                 {:text (get-translation "text.enhance-with-ai.refine" "Refine text")
                  :icon [icons/mux-pencil]
                  :on-click (fn []
                              (mixpanel/track "AI Text Enhance" {:Action "Refine text"})
                              (re-frame/dispatch [::enhance-text form-id context-id field-id :refine]))}]
                [dropdown/dropdown-menu-item
                 {:text (get-translation "text.enhance-with-ai.shorten" "Shorten")
                  :icon [icons/mux-shorten]
                  :on-click (fn []
                              (mixpanel/track "AI Text Enhance" {:Action "Shorten"})
                              (re-frame/dispatch [::enhance-text form-id context-id field-id :shorten]))}]
                [dropdown/dropdown-menu-item
                 {:text (get-translation "text.enhance-with-ai.elaborate" "Elaborate")
                  :icon [icons/mux-elaborate]
                  :on-click (fn []
                              (mixpanel/track "AI Text Enhance" {:Action "Elaborate"})
                              (re-frame/dispatch [::enhance-text form-id context-id field-id :elaborate]))}]]]
    (if disabled?
      [tooltip/mux-tooltip
       {}
       tooltip-text
       (fn [props]
         [:span props button])]
      button)))

(defn- ai-enhancement-footer-content
  "Renders the footer content for AI text enhancement feature.
  Shows loading state, enhancement results, or the enhancement dropdown based on current state."
  [{:keys [form-id context-id field-id current-text can-edit?
           ai-loading? ai-enhanced-text ai-consent?
           user-prompt prev-state-id next-state-id
           loading-ref-atom]}]
  (when ai-consent?
    (cond
      (or ai-loading? ai-enhanced-text)
      [ai-enhancement-footer
       {:form-id form-id
        :context-id context-id
        :field-id field-id
        :text ai-enhanced-text
        :loading? ai-loading?
        :user-prompt user-prompt
        :prev-state-id prev-state-id
        :next-state-id next-state-id
        :loading-ref #(reset! loading-ref-atom %)}]


      :else
      [:div.ai-dropdown-container
       [enhance-with-ai-dropdown
        {:form-id form-id
         :context-id context-id
         :field-id field-id
         :text current-text
         :has-permissions? can-edit?}]])))

(defn get-ai-enhancement-state
  "Returns a map containing all AI enhancement state and footer content for a given field.
  This is the main entry point used by markdown editors across the application."
  [form-id context-id field-id current-text can-edit? loading-ref]
  (let [ai-state @(re-frame/subscribe [::ai-enhancement-state form-id context-id field-id])
        ai-loading? (:loading? ai-state)
        ai-enhanced-text (:enhanced-text ai-state)
        ai-consent? @(re-frame/subscribe [::subs/generative-ai-consent?])
        user-prompt @(re-frame/subscribe [::ai-enhancement-current-user-prompt form-id context-id field-id])
        prev-state-id @(re-frame/subscribe [::ai-enhancement-prev-state-id form-id context-id field-id])
        next-state-id @(re-frame/subscribe [::ai-enhancement-next-state-id form-id context-id field-id])]
    {:ai-loading? ai-loading?
     :ai-enhanced-text ai-enhanced-text
     :ai-consent? ai-consent?
     :footer-content [ai-enhancement-footer-content
                      {:form-id form-id
                       :context-id context-id
                       :field-id field-id
                       :current-text current-text
                       :can-edit? can-edit?
                       :ai-loading? ai-loading?
                       :ai-enhanced-text ai-enhanced-text
                       :ai-consent? ai-consent?
                       :user-prompt user-prompt
                       :prev-state-id prev-state-id
                       :next-state-id next-state-id
                       :loading-ref-atom loading-ref}]}))
